import { createCompanionClient } from "@jurisui/companion/client";
import { createCompanionWorkspace } from "@jurisui/companion/workspace";
import type { CompanionConversationDetail, CompanionConversationSummary, CompanionDocumentOperation, CompanionLaunchTarget } from "@jurisui/companion/types";
import type { ArchitectureContext, ArchitectureSnapshot } from "./architecture-model";
import type { DiagramRecipe, DiagramGenerationRequest } from "./diagram-types";
import type { AppConfig, LiveDocumentSnapshotV1, TurnResponse } from "./types";

const DISCLOSURE = "I am Manolo, an AI assistant using a synthetic voice. This conversation may be recorded and processed by approved service providers.";

export interface ManoloRuntime {
  readonly testMode: boolean;
  config(): Promise<AppConfig>;
  launchTargets(): Promise<CompanionLaunchTarget[]>;
  architecture(revision?: string | number): Promise<ArchitectureSnapshot>;
  generateDiagram(input: DiagramGenerationRequest): Promise<DiagramRecipe>;
  createSession(): Promise<{ appSessionId: string; document: LiveDocumentSnapshotV1 }>;
  turn(input: { appSessionId: string; clientRequestId: string; input: string; source: "text" | "voice"; baseRevision: number; architecture?: ArchitectureContext; diagramRequest?: { kind?: DiagramRecipe["kind"]; scope: "selected"; mode: DiagramRecipe["mode"]; diagramId?: string; responseIntent?: "explain" | "diagram"; displayLevel?: "overview" | "technical"; focus?: import("./diagram-types").DiagramFocus } }): Promise<TurnResponse>;
  updateDocument(input: { appSessionId: string; clientRequestId: string; baseRevision: number; operations: CompanionDocumentOperation[] }): Promise<{ document: LiveDocumentSnapshotV1 }>;
  savedDocuments(): Promise<CompanionConversationSummary[]>;
  openDocument(id: string): Promise<CompanionConversationDetail>;
  saveDocument(title?: string): Promise<CompanionConversationSummary>;
  renameDocument(title: string): Promise<CompanionConversationSummary>;
  copyDocument(title: string, revision: number): Promise<CompanionConversationSummary & { document: LiveDocumentSnapshotV1 }>;
  createRealtimeSession(input: { appSessionId: string; disclosureAccepted: true; sdp: string }): Promise<unknown>;
  releaseRealtimeSession(): Promise<void>;
  releaseSession(): Promise<void>;
}

async function jsonRequest<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, { ...init, headers: { "content-type": "application/json", ...(init.headers ?? {}) } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw Object.assign(new Error(body.error?.message ?? body.error ?? response.statusText), {
    status: response.status, code: body.error?.code ?? body.code,
  });
  return (body.data ?? body) as T;
}

export function createManoloRuntime(getAccessToken: () => Promise<string | null>): ManoloRuntime {
  const testMode = import.meta.env.VITE_LIVE_DOCS_TEST_MODE === "true";
  if (testMode) {
    let sessionId: string | null = null;
    const post = <T>(url: string, body: unknown) => jsonRequest<T>(url, { method: "POST", body: JSON.stringify(body) });
    return {
      testMode: true,
      config: () => jsonRequest<AppConfig>("/api/config"),
      launchTargets: async () => [],
      architecture: () => jsonRequest<ArchitectureSnapshot>("/api/architecture"),
      generateDiagram: async (input) => (await post<{ diagram: DiagramRecipe }>("/api/architecture/diagrams", input)).diagram,
      async createSession() {
        const result = await post<{ appSessionId: string; document: LiveDocumentSnapshotV1 }>("/api/app-session", { disclosureAccepted: true });
        sessionId = result.appSessionId;
        return result;
      },
      turn: (input) => post<TurnResponse>("/api/turn", input),
      updateDocument: (input) => post("/api/document", input),
      savedDocuments: () => jsonRequest("/api/documents"),
      async openDocument(id) { const result = await jsonRequest<CompanionConversationDetail>(`/api/documents/${encodeURIComponent(id)}`); sessionId = result.id; return result; },
      saveDocument: (title) => post("/api/documents/save", { appSessionId: sessionId, title }),
      renameDocument: (title) => post("/api/documents/rename", { appSessionId: sessionId, title }),
      async copyDocument(title, revision) { const result = await post<CompanionConversationSummary & { document: LiveDocumentSnapshotV1 }>("/api/documents/copy", { appSessionId: sessionId, title, expectedRevision: revision }); sessionId = result.id; return result; },
      createRealtimeSession: (input) => post("/api/session", input),
      async releaseRealtimeSession() { if (sessionId) await post("/api/realtime/release", { appSessionId: sessionId }); },
      async releaseSession() { if (sessionId) await post("/api/session/release", { appSessionId: sessionId }); sessionId = null; },
    };
  }

  const identityUrl = String(import.meta.env.VITE_IDENTITY_SUPABASE_URL ?? "").replace(/\/$/, "");
  const apiKey = String(import.meta.env.VITE_IDENTITY_SUPABASE_ANON_KEY ?? import.meta.env.VITE_IDENTITY_SUPABASE_PUBLISHABLE_KEY ?? "");
  const baseUrl = String(import.meta.env.VITE_CONTROL_PLANE_URL ?? `${identityUrl}/functions/v1/control-plane`).replace(/\/$/, "");
  if (!identityUrl || !apiKey || !baseUrl) throw new Error("The central Juris identity or Control Plane configuration is missing");
  const companion = createCompanionClient({ baseUrl, apiKey, getAccessToken });
  const workspace = createCompanionWorkspace(companion, { app: "live-docs", route: () => window.location.pathname });
  return {
    testMode: false,
    launchTargets: () => companion.launchTargets(),
    architecture: (revision) => companion.architecture<ArchitectureSnapshot>("live-docs", "juris-astra", revision),
    async generateDiagram(input) {
      const result = await companion.generateDiagram({ ...input, architecture: { ...input.architecture }, app: "live-docs" });
      return result.diagram as DiagramRecipe;
    },
    async config() {
      const context = await companion.context("live-docs", window.location.pathname);
      if (!context.assistant?.enabled || context.assistant.agentKey !== "companion-agent" || context.settings?.askEnabled === false) {
        throw new Error("Manolo is unavailable for Live Docs. A Juris administrator must enable its application binding and approved provider.");
      }
      return {
        disclosure: DISCLOSURE, knowledgeFileCount: 0, productName: "Live Docs",
        profile: context.assistant.identity ?? "Manolo", wakePhrase: "Hey Manolo",
        documentTitle: "Architecture document", exportStem: "juris-architecture",
        starterPrompts: (context.settings?.suggestedPrompts ?? []).map((prompt) => typeof prompt === "string" ? prompt : prompt.prompt),
        testMode: false,
      };
    },
    async createSession() { const result = await workspace.create(); return { appSessionId: result.conversationId, document: result.document }; },
    async turn(input) {
      const result = await workspace.turn({ message: input.input,
        pageContext: { route: window.location.pathname, ...(input.architecture ? { architecture: input.architecture as unknown as Record<string, unknown> } : {}), ...(input.diagramRequest ? { diagramRequest: input.diagramRequest } : {}) },
        clientRequestId: input.clientRequestId, baseRevision: input.baseRevision, source: input.source });
      if (!result.turn || !result.document) throw new Error("Manolo returned no validated document result.");
      return { mode: "answer", turn: result.turn, document: result.document };
    },
    updateDocument: (input) => workspace.update(input.operations, input.clientRequestId, input.baseRevision),
    savedDocuments: () => companion.savedConversations("live-docs"),
    openDocument: (id) => workspace.open(id),
    saveDocument: (title) => workspace.save(title),
    renameDocument: (title) => workspace.rename(title),
    copyDocument: (title, revision) => workspace.saveAs(title, revision),
    async createRealtimeSession(input) {
      const token = await getAccessToken();
      if (!token) throw new Error("Sign in with your ICC account before starting voice.");
      return jsonRequest(`${baseUrl}/companion/realtime/session`, { method: "POST", headers: { authorization: `Bearer ${token}`, apikey: apiKey },
        body: JSON.stringify({ app: "live-docs", disclosureAccepted: input.disclosureAccepted, sdp: input.sdp }) });
    },
    async releaseRealtimeSession() { /* The central voice transport is closed by its shared session channel. */ },
    async releaseSession() { workspace.reset(); },
  };
}
